export const POSTPAID_CREDIT_MAX = 1000000;

export function formatCreditLimit(value: unknown) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '';
  return amount.toLocaleString('en-IN');
}

export function postpaidCreditCeiling(parentPostpaidCredit: number | null | undefined) {
  if (parentPostpaidCredit == null || !Number.isFinite(parentPostpaidCredit)) {
    return POSTPAID_CREDIT_MAX;
  }
  return Math.min(POSTPAID_CREDIT_MAX, Math.floor(parentPostpaidCredit));
}

export type LimitPeriod = 'daily' | 'weekly' | 'monthly';

function wholeLimit(value: string | number, ceiling: number, label: string) {
  const amount = Number(value);
  if (!Number.isInteger(amount) || amount < 1 || amount > ceiling) {
    return `${label} must be a whole number from 1 to ${formatCreditLimit(ceiling)}`;
  }
  return '';
}

export function billingPlanFormError(
  billingType: string,
  postpaidMode: string,
  parentIsPrepaid = false,
  parentPostpaidCredit: number | null = null,
  limitPeriod = '',
  limitAmount: string | number = '',
  parentLimitPeriod: LimitPeriod | null = null,
) {
  if (parentIsPrepaid && billingType === 'postpaid') {
    return 'A prepaid company cannot create a postpaid company';
  }
  if (billingType !== 'postpaid' && billingType !== 'prepaid') {
    return 'Please select a billing plan';
  }
  if (billingType === 'prepaid') return '';
  if (postpaidMode !== 'unlimited' && postpaidMode !== 'limited') {
    return 'Please select unlimited or limited postpaid';
  }
  const ceiling = postpaidCreditCeiling(parentPostpaidCredit);
  if (postpaidMode === 'unlimited') {
    if (parentLimitPeriod) {
      return `This company can only assign a ${parentLimitPeriod} postpaid limit`;
    }
    if (POSTPAID_CREDIT_MAX > ceiling) {
      return `Unlimited postpaid is 10 lac, which is more than this company's ${formatCreditLimit(ceiling)} credits`;
    }
    return '';
  }
  if (parentLimitPeriod && limitPeriod && limitPeriod !== parentLimitPeriod) {
    return `This company can only assign a ${parentLimitPeriod} limit`;
  }
  if (limitPeriod !== 'daily' && limitPeriod !== 'weekly' && limitPeriod !== 'monthly') {
    return 'Please select daily, weekly, or monthly';
  }
  if (parentLimitPeriod && limitPeriod !== parentLimitPeriod) {
    return `This company can only assign a ${parentLimitPeriod} limit`;
  }
  const label =
    limitPeriod === 'daily' ? 'Daily limit' : limitPeriod === 'weekly' ? 'Weekly limit' : 'Monthly limit';
  return wholeLimit(limitAmount, ceiling, label);
}

export function billingPlanLabel(company: {
  billing_type?: string | null;
  postpaid_mode?: string | null;
  credit_limit?: unknown;
  billing_cycle?: string | null;
} | null | undefined) {
  if (!company?.billing_type) return '—';
  if (company.billing_type === 'prepaid') return 'Prepaid';
  if (company.billing_type === 'postpaid' && company.postpaid_mode === 'unlimited') {
    return 'Unlimited Postpaid (10 lac)';
  }
  if (company.billing_type === 'postpaid' && company.postpaid_mode === 'limited') {
    const amount = formatCreditLimit(company.credit_limit);
    const cycle = company.billing_cycle;
    if (cycle && amount) return `Limited Postpaid (${cycle} ${amount})`;
    return amount ? `Limited Postpaid (${amount})` : 'Limited Postpaid';
  }
  return '—';
}
